import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  FileSpreadsheet, 
  Crown, 
  Trash2, 
  ChevronRight, 
  ArrowRight, 
  Search,
  MapPin,
  Phone,
  Mail,
  User,
  Home,
  DollarSign,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Filter,
  Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImportedListsPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [movingLeads, setMovingLeads] = useState(false);

  const tier = subscription?.tier || "starter";

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/imported-lists`, { 
        withCredentials: true 
      });
      setLists(response.data);
    } catch (error) {
      console.error("Failed to fetch lists:", error);
      toast.error("Failed to load imported lists");
    } finally {
      setLoading(false);
    }
  };

  const fetchListDetails = async (listId) => {
    setLoadingList(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/imported-lists/${listId}`, { 
        withCredentials: true 
      });
      setSelectedList(response.data);
      setSelectedLeads([]);
    } catch (error) {
      toast.error("Failed to load list details");
    } finally {
      setLoadingList(false);
    }
  };

  const deleteList = async (listId) => {
    if (!confirm("Are you sure you want to delete this list?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/imported-lists/${listId}`, { 
        withCredentials: true 
      });
      setLists(lists.filter(l => l.list_id !== listId));
      if (selectedList?.list_id === listId) {
        setSelectedList(null);
      }
      toast.success("List deleted");
    } catch (error) {
      toast.error("Failed to delete list");
    }
  };

  const moveSelectedToPipeline = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Please select leads to move");
      return;
    }

    setMovingLeads(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/imported-lists/${selectedList.list_id}/move-to-pipeline`,
        { lead_ids: selectedLeads },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          toast.warning(`Lead skipped: ${err.error}`);
        });
      }
      
      // Refresh the list
      fetchListDetails(selectedList.list_id);
      fetchLists();
      setSelectedLeads([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move leads");
    } finally {
      setMovingLeads(false);
    }
  };

  const toggleLeadSelection = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const selectAllLeads = () => {
    const availableLeads = filteredLeads.filter(l => l.status !== "moved_to_pipeline");
    if (selectedLeads.length === availableLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(availableLeads.map(l => l.lead_id));
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Filter leads
  const filteredLeads = selectedList?.leads?.filter(lead => {
    const matchesSearch = searchQuery === "" || 
      lead.property_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.city?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) || [];

  // Feature gate
  if (tier === "starter") {
    return (
      <div className="min-h-screen bg-black flex">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={`${mainClass} p-4 md:p-8`}>
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-6">
              <FileSpreadsheet className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-4">Imported Lists</h1>
            <p className="text-white/70 mb-8">Import and manage lead lists separately from your pipeline.</p>
            <Link to="/upgrade">
              <Button className="gold-gradient-btn rounded-none font-bold px-8 h-12">
                <Crown className="w-5 h-5 mr-2" />Upgrade to Pro
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} flex-1 p-4 md:p-8`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Imported Lists</h1>
            <p className="text-white/70 mt-1">Manage your imported leads before adding to pipeline</p>
          </div>
          <Link to="/import">
            <Button className="gold-gradient-btn rounded-none font-bold">
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Import New List
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : selectedList ? (
          /* List Detail View */
          <div>
            {/* Back button and list info */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => setSelectedList(null)}
                className="flex items-center gap-2 text-white/70 hover:text-gold"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Lists
              </button>
              
              <div className="flex items-center gap-4">
                {selectedLeads.length > 0 && (
                  <Button 
                    onClick={moveSelectedToPipeline}
                    disabled={movingLeads}
                    className="gold-gradient-btn rounded-none font-bold"
                  >
                    {movingLeads ? "Moving..." : `Move ${selectedLeads.length} to Pipeline`}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>

            {/* List Header */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-xl font-bold text-white">{selectedList.name}</h2>
                  <p className="text-white/70 text-sm mt-1">
                    {selectedList.total_leads} total leads • {selectedList.leads_moved} moved to pipeline
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white/70 text-sm">Imported</p>
                  <p className="text-white">{formatDate(selectedList.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                <Input
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] text-white px-4 py-2"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="moved_to_pipeline">Moved to Pipeline</option>
              </select>

              <Button 
                variant="outline" 
                onClick={selectAllLeads}
                className="border-white/[0.1] text-white/70 hover:bg-white/[0.06]"
              >
                {selectedLeads.length === filteredLeads.filter(l => l.status !== "moved_to_pipeline").length 
                  ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {/* Leads Table */}
            {loadingList ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black">
                      <tr>
                        <th className="p-3 text-left w-10"></th>
                        <th className="p-3 text-left text-white/70">Address</th>
                        <th className="p-3 text-left text-white/70">Owner</th>
                        <th className="p-3 text-left text-white/70">Contact</th>
                        <th className="p-3 text-left text-white/70">Price</th>
                        <th className="p-3 text-left text-white/70">Status</th>
                        <th className="p-3 text-left text-white/70">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {filteredLeads.map((lead) => {
                        const isMoved = lead.status === "moved_to_pipeline";
                        return (
                          <tr 
                            key={lead.lead_id} 
                            className={`hover:bg-white/[0.06]/50 ${isMoved ? 'opacity-50' : ''}`}
                          >
                            <td className="p-3">
                              {!isMoved && (
                                <Checkbox
                                  checked={selectedLeads.includes(lead.lead_id)}
                                  onCheckedChange={() => toggleLeadSelection(lead.lead_id)}
                                />
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Home className="w-4 h-4 text-gold flex-shrink-0" />
                                <div>
                                  <p className="text-white font-medium">{lead.property_address}</p>
                                  <p className="text-white/70 text-xs">
                                    {[lead.city, lead.state, lead.zip_code].filter(Boolean).join(", ")}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-white/70" />
                                <span className="text-white">{lead.owner_name || "-"}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="space-y-1">
                                {lead.owner_phone && (
                                  <div className="flex items-center gap-1 text-white/70 text-xs">
                                    <Phone className="w-3 h-3" />
                                    {lead.owner_phone}
                                  </div>
                                )}
                                {lead.owner_email && (
                                  <div className="flex items-center gap-1 text-white/70 text-xs">
                                    <Mail className="w-3 h-3" />
                                    {lead.owner_email}
                                  </div>
                                )}
                                {!lead.owner_phone && !lead.owner_email && (
                                  <span className="text-white/70">-</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-white font-mono">
                                {formatCurrency(lead.asking_price)}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 text-xs font-medium ${
                                lead.status === "moved_to_pipeline" 
                                  ? "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]"
                                  : lead.status === "qualified"
                                  ? "bg-gold/20 text-gold"
                                  : lead.status === "contacted"
                                  ? "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]"
                                  : "bg-white/[0.1]/50 text-white/70"
                              }`}>
                                {lead.status === "moved_to_pipeline" ? "In Pipeline" : 
                                 lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                              </span>
                            </td>
                            <td className="p-3">
                              {!isMoved ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedLeads([lead.lead_id]);
                                    moveSelectedToPipeline();
                                  }}
                                  className="text-gold hover:text-gold-light hover:bg-gold/10"
                                >
                                  <ArrowRight className="w-4 h-4 mr-1" />
                                  Move
                                </Button>
                              ) : (
                                <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] flex items-center gap-1">
                                  <CheckCircle className="w-4 h-4" />
                                  Done
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {filteredLeads.length === 0 && (
                  <div className="text-center py-8 text-white/70">
                    No leads found matching your filters
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Lists Overview */
          <div>
            {lists.length === 0 ? (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <FileSpreadsheet className="w-16 h-16 text-white/70 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-white mb-2">No Imported Lists</h3>
                <p className="text-white/70 mb-6">Import your first list to manage leads separately from your pipeline.</p>
                <Link to="/import">
                  <Button className="gold-gradient-btn rounded-none font-bold">
                    <FileSpreadsheet className="w-5 h-5 mr-2" />
                    Import Your First List
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {lists.map((list) => (
                  <div 
                    key={list.list_id}
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 hover:border-gold/50 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => fetchListDetails(list.list_id)}
                      >
                        <h3 className="font-heading text-lg font-bold text-white mb-1">
                          {list.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-white/70">
                          <span>{list.total_leads} leads</span>
                          <span>•</span>
                          <span>{list.leads_moved} moved to pipeline</span>
                          <span>•</span>
                          <span>{formatDate(list.created_at)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => fetchListDetails(list.list_id)}
                          className="gold-gradient-btn rounded-none"
                        >
                          View Leads
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteList(list.list_id)}
                          className="text-[#FF3B30] hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-white/70 mb-1">
                        <span>Pipeline Progress</span>
                        <span>{Math.round((list.leads_moved / list.total_leads) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-white/[0.08] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gold transition-all"
                          style={{ width: `${(list.leads_moved / list.total_leads) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
